import { MaestroAPI, maestroApiCall } from '@michelin/central-provider';
import { MethodType } from '@michelin/central-provider/dist/helpers/createAPICall';
import { ContactListingOVM, ContactOVM, NotificationSubscriptionOVM, ResponseOVM } from '@michelin/fcp-view-models';
import { useQuery } from '@tanstack/react-query';
import { auth } from 'Auth';
import { contactQuery } from 'components/Contact/utils';
import GLOBAL_CONFIG from '../global-variables';

const baseURL = GLOBAL_CONFIG.externalAPIUrl;

export type ContactListingWithNotifications = ContactListingOVM & {
  notification_subscription?: NotificationSubscriptionOVM[];
};

export async function deleteContacts({ fleetId, ids }: { fleetId: string; ids: string[] }) {
  const apiType: MaestroAPI = { method: MethodType.DELETE, endpoint: `cp-be/contacts/${fleetId}`, baseURL };

  const response = await maestroApiCall(apiType, { contact_ids: ids.join(',') }).then((res) => res.data);
  return response;
}

async function getContactsForRole(
  apiType: MaestroAPI,
  role: string,
  customerType?: string,
): Promise<ContactListingOVM[]> {
  const res = await maestroApiCall<ResponseOVM<ContactListingOVM[]>>(apiType, {
    role,
    contact_level: customerType?.toUpperCase() ?? '',
  });
  return res?.data ?? [];
}

async function getNotificationSubscriptions(contactId: string): Promise<NotificationSubscriptionOVM[]> {
  if (!auth.apolloClient) return [];

  try {
    const response = await auth.apolloClient.query<{ getContactByKey: ContactOVM }>({
      query: contactQuery,
      variables: { hash_key: `2~${contactId}`, range_key: 'v0_contact' },
      fetchPolicy: 'no-cache',
    });

    return response.data.getContactByKey.notification_subscription ?? [];
  } catch (error) {
    console.error(`Unable to load notification preferences for contact ${contactId}.`, error);
    return [];
  }
}

async function getContacts(
  fleetId: string,
  roleAreas: string[],
  customerType?: string,
): Promise<ContactListingWithNotifications[]> {
  const apiType: MaestroAPI = { method: MethodType.GET, endpoint: `cp-be/contacts/${fleetId}`, baseURL };

  const contactsByRole = await Promise.all(roleAreas.map((role) => getContactsForRole(apiType, role, customerType)));

  const contactsById = new Map<string, ContactListingOVM>();
  contactsByRole.flat().forEach((contact) => {
    if (contact.id) contactsById.set(contact.id, contact);
  });

  return Promise.all(
    Array.from(contactsById.values()).map(async (contact) => ({
      ...contact,
      notification_subscription: await getNotificationSubscriptions(contact.id as string),
    })),
  );
}

export function useContactsList(fleetId?: string, roleAreas: string[] = [], customerType?: string) {
  return useQuery(
    ['contacts-list', fleetId, roleAreas, customerType],
    () => getContacts(fleetId as string, roleAreas, customerType),
    {
      enabled: !!fleetId && roleAreas.length > 0,
    },
  );
}
